import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {
  Field,
  reduxForm,
  formValueSelector,
  updateSyncWarnings,
  stopAsyncValidation
} from 'redux-form';
import classNames from 'classnames';

import {SelectField, DateField, TextAreaField} from 'components/Fields';
import FormField from 'components/FormField';
import Label from 'components/Label';
import ErrorBlock from 'components/ErrorBlock';
import DisabledDatePicker from 'components/DisabledDatePicker';

import {MODALS, predictTermination} from 'ducks/tenancy';
import {
  selectModal,
  closeModal,
  openModal,
  storeDataInModal
} from 'ducks/modals';

import {required, validDate} from 'utils/validators';
import {formatDateForApi} from 'utils/datetime';
import {getErrors} from 'utils/errors';
import {getWarnings} from 'utils/warnings';

import {validateTenancyCanBeTerminated} from 'sdk/tenancy';

import './styles.css';

const formSubmit = (values, dispatch, props) => {
  const {
    terminationReason,
    terminationDate,
    departureDate,
    notes,
    terminatedBeforeMoveIn
  } = values;
  const submitData = {
    reason: terminationReason,
    terminationDate: formatDateForApi(terminationDate),
    departureDate: formatDateForApi(departureDate),
    notes,
    terminatedBeforeMoveIn,
    tenancyId: props.tenancyId
  };

  const extraActions = [
    () => closeModal(MODALS.TERMINATE_TENANCY),
    () => openModal(MODALS.CONFIRM_TERMINATION),
    data =>
      storeDataInModal({
        modalName: MODALS.CONFIRM_TERMINATION,
        data: {
          predictData: data,
          submitData
        }
      })
  ];

  dispatch(predictTermination(submitData, extraActions));
};

const constructSelectOption = reason => {
  return {value: reason.id, label: reason.name};
};

const asyncValidateTenancyCanBeTerminated = (values, dispatch, props) => {
  const {terminatedBeforeMoveIn, terminationDate, departureDate} = values;
  const data = {
    terminationDate: formatDateForApi(terminationDate),
    departureDate: formatDateForApi(departureDate),
    terminatedBeforeMoveIn: terminatedBeforeMoveIn
  };

  return validateTenancyCanBeTerminated({
    tenancyId: props.tenancyId,
    ...data
  })
    .then(() => {
      dispatch(updateSyncWarnings('terminateTenancy', {}, {}));
    })
    .catch(error => {
      if (error.response.status === 400) {
        const responseData = error.response.data;

        if (_.get(responseData, 'warnings')) {
          const warnings = getWarnings(responseData.warnings);

          dispatch(updateSyncWarnings('terminateTenancy', warnings, {}));
          return dispatch(stopAsyncValidation('terminateTenancy'));
        }

        dispatch(updateSyncWarnings('terminateTenancy', {}, {}));

        const errors = getErrors(responseData);

        if (_.get(errors, '_error')) {
          errors._error = errors._error.join(' ');
        }

        return errors;
      }
    });
};

class TerminateTenancyForm extends React.Component<any> {
  render() {
    const {
      className,
      handleSubmit,
      error,
      terminationReasons,
      terminatedBeforeMoveInValue
    } = this.props;

    const terminationReasonsMapped = terminationReasons.map(reason => {
      return constructSelectOption(reason);
    });

    return (
      <div className="formContainer">
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <form
          onSubmit={handleSubmit}
          className={classNames(className, 'TerminateTenancyForm')}>
          <FormField>
            <Label>Termination Reason</Label>
            <Field
              name="terminationReason"
              component={SelectField}
              validate={[required]}
              normalize={value => value && value.value}
              {...{options: terminationReasonsMapped}}
            />
          </FormField>
          <FormField>
            <Label>Early departure date</Label>
            {!terminatedBeforeMoveInValue && (
              <Field
                name="departureDate"
                component={DateField}
                validate={[validDate]}
              />
            )}
            {terminatedBeforeMoveInValue && (
              <DisabledDatePicker placeholder="DD/MM/YYYY" />
            )}
          </FormField>
          <FormField>
            <Label>License termination date</Label>
            <Field
              name="terminationDate"
              component={DateField}
              validate={[validDate]}
            />
          </FormField>

          <FormField>
            <Label>Notes</Label>
            <Field
              name="notes"
              component={TextAreaField}
              validate={[required]}
              placeholder="Write your notes here..."
            />
          </FormField>
        </form>
      </div>
    );
  }
}

const selector = formValueSelector('terminateTenancy');
const mapStateToProps = state => {
  const {data: {terminationReasons}} = selectModal(
    state,
    MODALS.TERMINATE_TENANCY
  );

  return {
    terminationReasons: terminationReasons || [],
    initialValues: {
      terminatedBeforeMoveIn: false
    },
    terminatedBeforeMoveInValue: selector(state, 'terminatedBeforeMoveIn'),
    terminationDateValue: selector(state, 'terminationDate'),
    departureDateValue: selector(state, 'departureDate')
  };
};

export default connect(mapStateToProps, null)(
  reduxForm({
    form: 'terminateTenancy',
    onSubmit: formSubmit,
    enableReinitialize: true,
    asyncValidate: asyncValidateTenancyCanBeTerminated,
    asyncChangeFields: [
      'terminatedBeforeMoveIn',
      'terminationDate',
      'departureDate'
    ]
  })(TerminateTenancyForm)
);
